import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AnalyticsService } from '@core/services/analytics.service';
import { select, Store } from '@ngrx/store';
import { IResultsState } from '@results/store';
import {
  selectDestination,
  selectFiltersApplied,
  selectPagination,
} from '@results/store/results.selector';
import { Ga4Service } from '@services/ga4.service';
import { UrlService } from '@services/url.service';
import {
  selectContextCore,
  selectDeviceIsMobile,
  selectIsLanding,
} from '@store/context/context.selector';
import { IContextCore } from '@store/context/context.state';
import { IHotelsAppState } from '@store/index';
import { selectQuery } from '@store/router/router.selector';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { HotelComponent } from '../hotel/hotel.component';
import { AsyncPipe } from '@angular/common';

export const ArgentinaId = '1123215';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'am-hotels-list',
  styleUrls: ['./hotel-list.component.scss'],
  templateUrl: './hotels-list.component.html',
  imports: [HotelComponent, AsyncPipe],
})
export class HotelsListComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Input()
  public hotels: any;
  public unSubscribe$ = new Subject<void>();
  public query$: Observable<any>;
  public isLanding$: Observable<boolean>;
  public isMobile$: Observable<boolean>;
  public locale$: Observable<IContextCore['locale']>;
  private destination$: Observable<any>;

  public defaultImage: string;
  public urlResize: string;
  public nearByFilter: any;
  public appliedFilters: any;
  private brands: string[] = ['avantrip'];
  public brand = 'almundo';
  public channel = 'web';
  public isArgentina = false;

  constructor(
    private store$: Store<IHotelsAppState>,
    private urlService: UrlService,
    private detectionRef: ChangeDetectorRef,
    private analyticsService: AnalyticsService,
    private hotelsResults: Store<IResultsState>,
    private ga4Service?: Ga4Service,
  ) {}

  public ngOnInit() {
    this.initialize();
    this.destination$.subscribe((destination) => {
      this.isArgentina = destination.country.id === ArgentinaId;
    });
  }

  public ngAfterViewChecked() {
    this.detectionRef.markForCheck();
  }

  public initialize() {
    this.initDefaultImage();
    this.initNearByFilter();
    this.getEconomicHotel();
    this.urlResize = this.urlService.getResizedUrl();
    this.isMobile$ = this.store$.pipe(select(selectDeviceIsMobile));
    this.query$ = this.store$.pipe(
      select(selectQuery),
      filter((data) => data !== undefined),
    );
    this.isLanding$ = this.store$.pipe(
      select(selectIsLanding),
      filter((isLanding) => isLanding !== undefined),
    );
    if (this.ga4Service !== undefined) {
      this.ga4Service.view_item_list();
    }
    this.destination$ = this.hotelsResults.pipe(
      select(selectDestination),
      takeUntil(this.unSubscribe$),
    );
  }

  public initNearByFilter() {
    this.store$
      .pipe(select(selectFiltersApplied), takeUntil(this.unSubscribe$))
      .subscribe((filters) => {
        if (filters && filters.length) {
          this.appliedFilters = filters;
          const nearByAppliedFilter: any = filters.find(
            (appliedFilter: any) => appliedFilter.type === 'NEARBY',
          );
          this.nearByFilter =
            nearByAppliedFilter && nearByAppliedFilter.values
              ? nearByAppliedFilter.values[0]
              : null;
        }
      });
  }

  public initDefaultImage() {
    this.store$
      .pipe(select(selectContextCore), takeUntil(this.unSubscribe$))
      .subscribe((context) => {
        this.brand = context.brand;
        this.channel = context.channel;
        if (this.brands.includes(context.brand)) {
          this.defaultImage = `/hotels/dist/es-AR/assets/images/${context.brand}-hotel-no-image.svg`;
        } else {
          this.defaultImage = '/hotels/dist/es-AR/assets/images/almundo-hotel-no-image.svg';
        }
      });
  }

  public trackByItems(_index, item) {
    return item.id;
  }

  public trackContactRowClick() {
    // TODO MR analytics
  }

  public getEconomicHotel() {
    let offset: any;
    this.store$
      .pipe(select(selectPagination))
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((pagination: any) => {
        offset = pagination.offset;
      });

    this.store$.pipe(select(selectQuery), takeUntil(this.unSubscribe$)).subscribe((query: any) => {
      if (query && !query.featuredHotelId && offset === 0 && this.hotels) {
        const hotelEconomic = {
          price: Infinity,
          ref: null,
        };

        this.hotels.forEach((hotel) => {
          if (hotel && hotel.stars && hotel.stars > 3) {
            if (
              hotel.rate &&
              hotel.rate.price.price_per_night_per_room &&
              hotel.rate.price.price_per_night_per_room < hotelEconomic.price
            ) {
              hotelEconomic.price = hotel.rate.price.price_per_night_per_room;
              hotelEconomic.ref = hotel;
            }
          }
        });

        if (hotelEconomic.ref) {
          this.analyticsService.trackEvent({
            action: 'Economic 4 o 5 estrellas',
            label: 'Show',
            nonInteraction: true,
            section: 'Results',
          });

          let findFilter = false;

          if (this.appliedFilters) {
            findFilter = this.appliedFilters.find((appliedFilter) => {
              if (appliedFilter.type === 'STAR' || appliedFilter.type === 'MEAL') {
                return appliedFilter.values.find((value) => {
                  return value.code === 'ALL_INCLUSIVE' || value.code === '4' || value.code === '5';
                });
              }
            });
          }

          hotelEconomic.ref = {
            ...hotelEconomic.ref,
            isEconomic: {
              withAppliedFilters: findFilter,
            },
          };

          this.hotels = this.hotels.map((hotel: any) => {
            if (hotel.id === hotelEconomic.ref.id) {
              return { ...hotel, isEconomic: hotelEconomic.ref.isEconomic };
            } else {
              return hotel;
            }
          });
        }
      }
    });
  }

  public ngOnDestroy() {
    this.unSubscribe$.next();
    this.unSubscribe$.unsubscribe();
  }
}
